import { useNavigate } from 'react-router-dom';

function initials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

/**
 * StoryRing — profile avatar with optional lime ring indicating active story.
 * Props:
 *  userId, displayName, profilePhoto
 *  size (px)            default 80
 *  hasActiveStory (bool)
 *  hasUnviewedStory (bool)  (controls solid-vs-dim ring)
 *  onClick              custom tap handler; default opens viewer if has story, else noop
 *  testId
 *  clickable            if false, rendered statically
 */
export default function StoryRing({
  userId, displayName, profilePhoto,
  size = 80, hasActiveStory = false, hasUnviewedStory = false,
  onClick, testId, clickable = true,
}) {
  const navigate = useNavigate();

  const handle = () => {
    if (onClick) return onClick();
    if (hasActiveStory && userId) navigate(`/stories/${userId}`);
  };

  // Ring uses padding for a gap between ring and avatar.
  const ringPad = hasActiveStory ? 3 : 0;
  const inner = size - ringPad * 2;

  const ringClass = !hasActiveStory
    ? ''
    : hasUnviewedStory
      ? 'bg-[#B6E600]'
      : 'bg-[#D9D9D9]';

  // Render as a <div role="button"> instead of <button> so this component can
  // be safely nested inside other clickable parents (e.g. the StoriesRail item
  // wraps each ring in its own button). Avoids the
  // 'In HTML, <button> cannot be a descendant of <button>' hydration warning.
  const handleKey = (e) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handle(); }
  };
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      onClick={clickable ? handle : undefined}
      onKeyDown={clickable ? handleKey : undefined}
      aria-disabled={!clickable}
      data-testid={testId || `story-ring-${userId || 'me'}`}
      className={`relative rounded-full ${ringClass} active:scale-95 transition-transform ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
      style={{ width: size, height: size, padding: ringPad }}
      aria-label={hasActiveStory ? `View ${displayName || 'user'}'s story` : (displayName || 'Profile')}
    >
      <div
        className="rounded-full overflow-hidden bg-white"
        style={{ width: inner, height: inner, padding: hasActiveStory ? 2 : 0 }}
      >
        {profilePhoto ? (
          <img loading="lazy" decoding="async" src={profilePhoto} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          <div className="w-full h-full rounded-full bg-[#F1F0EA] flex items-center justify-center font-bold text-[#9B9EA3]" style={{ fontSize: Math.max(12, Math.round(size * 0.25)) }}>
            {initials(displayName)}
          </div>
        )}
      </div>
    </div>
  );
}
